import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Users, Star, BadgeCheck, Sparkles } from "lucide-react";
import { formatPrice, primaryImage, titleize } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

export default function VenueCard({ venue, index = 0 }) {
    const href = `/venues/${venue.country_slug}/${venue.city_slug}/${venue.slug}`;
    const img = primaryImage(venue);
    const cap = venue.capacity_standing || venue.capacity_seated;
    return (
        <Link
            to={href}
            className="group block rounded-xl overflow-hidden border border-border bg-card hover-card-lift fade-in-up"
            style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
            data-testid={`venue-card-${venue.venue_id}`}
        >
            <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                <img
                    src={img}
                    alt={venue.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                    {venue.is_featured && (
                        <Badge className="bg-secondary text-secondary-foreground border-0 gap-1"><Sparkles className="h-3 w-3" /> Featured</Badge>
                    )}
                    {venue.is_verified && (
                        <Badge className="bg-white/95 text-primary border-0 gap-1"><BadgeCheck className="h-3 w-3" /> Verified</Badge>
                    )}
                </div>
            </div>
            <div className="p-4 md:p-5">
                <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-lg md:text-xl font-semibold leading-tight tracking-tight">{venue.name}</h3>
                </div>
                <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" />
                    <span>{venue.area ? `${venue.area}, ${venue.city}` : venue.city}</span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{venue.short_description}</p>
                <div className="mt-4 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3 text-muted-foreground">
                        {cap ? (
                            <span className="inline-flex items-center gap-1"><Users className="h-4 w-4" /> up to {cap}</span>
                        ) : null}
                        <span className="text-xs uppercase tracking-wider text-muted-foreground/80">{titleize(venue.venue_type)}</span>
                    </div>
                    <div className="text-right">
                        <span className="font-semibold text-foreground">{formatPrice(venue.price_from, venue.price_currency)}</span>
                        {venue.price_from ? <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">from</span> : null}
                    </div>
                </div>
            </div>
        </Link>
    );
}
